import React, { useEffect, useMemo, useState } from 'react';
import { type CartItem, type CartJobType, type CartProduct, useCart } from '../../hooks/useCart';

interface StripeItem {
  price: string;
  quantity: number;
}

interface CheckoutRequest {
  authenticity_token: string;
  return_path: string;
  purchase_name: string;
  purchase_amount: number;
  prices: StripeItem[];
}

export interface RecruiterServiceCartProps {
  products: CartProduct[];
  job_types: CartJobType[];
  create_quote_path: string;
  form_authenticity_token: string;
  return_path: string;
  cancel_url: string;
}

const getJobTypeLabel = (jobType: CartJobType): string => jobType.name || jobType.title || '';

const CartItems: React.FC<{
  state: { items: CartItem[] };
  removeItem: (item: CartItem) => void;
  updateJobType: (id: number, jobType: CartJobType) => void;
  updateProduct: (id: number, product: CartProduct) => void;
  jobTypes: CartJobType[];
  products: CartProduct[];
  getItemPrice: (item: CartItem) => number;
}> = ({ state, removeItem, updateJobType, updateProduct, jobTypes, products, getItemPrice }) => {
  if (state.items.length === 0) {
    return (
      <div className="empty-cart">
        <i className="fa fa-shopping-cart fa-2x text-muted"></i>
        <p className="text-muted mt-2">Your cart is empty</p>
      </div>
    );
  }

  return (
    <div className="cart-items">
      {state.items.map(item => (
        <div key={item.id} className="cart-item border-bottom py-3">
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <h6>{item.product.name}</h6>
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label small">Service Type:</label>
                  <select
                    className="form-select form-select-sm"
                    value={item.jobType.id}
                    onChange={event => {
                      const selectedJobType = jobTypes.find(jt => jt.id === Number(event.target.value));
                      if (selectedJobType) {
                        updateJobType(item.id, selectedJobType);
                      }
                    }}
                  >
                    {jobTypes.map(jobType => (
                      <option key={jobType.id} value={jobType.id}>
                        {getJobTypeLabel(jobType)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Product:</label>
                  <select
                    className="form-select form-select-sm"
                    value={item.product.id}
                    onChange={event => {
                      const selectedProduct = products.find(product => product.id === Number(event.target.value));
                      if (selectedProduct) {
                        updateProduct(item.id, selectedProduct);
                      }
                    }}
                  >
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="text-success mt-1">
                <strong>${getItemPrice(item)}</strong>
                {item.jobType.premium && <span className="badge bg-warning text-dark ms-2">Premium</span>}
              </div>
            </div>
            <button
              className="btn btn-sm btn-outline-danger ms-3"
              onClick={() => removeItem(item)}
              aria-label="Remove item"
            >
              <i className="fa fa-trash"></i>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const ProductSelector: React.FC<{
  products: CartProduct[];
  jobTypes: CartJobType[];
  jobType: CartJobType | null;
  setJobType: React.Dispatch<React.SetStateAction<CartJobType | null>>;
  addItem: (product: CartProduct, jobType: CartJobType) => void;
}> = ({ products, jobTypes, jobType, setJobType, addItem }) => (
  <div className="product-selector">
    <div className="mb-3">
      <label className="form-label">Service Type:</label>
      <select
        className="form-select"
        value={jobType?.id ?? ''}
        onChange={event => {
          const selected = jobTypes.find(jt => jt.id === Number(event.target.value));
          if (selected) {
            setJobType(selected);
          }
        }}
      >
        {jobTypes.map(type => (
          <option key={type.id} value={type.id}>
            {getJobTypeLabel(type)}
          </option>
        ))}
      </select>
      {jobType?.premium && (
        <small className="text-warning">
          <i className="fa fa-star"></i> Premium service
        </small>
      )}
    </div>

    <div className="products-list">
      {products.map(product => {
        const price = jobType?.premium ? product.premium_price : product.price;

        return (
          <div key={product.id} className="product-item border rounded p-3 mb-2">
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <h6 className="mb-1">{product.name}</h6>
                {product.description && <small className="text-muted">{product.description}</small>}
                <div className="text-success mt-1">
                  <strong>${price}</strong>
                </div>
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  if (jobType) {
                    addItem(product, jobType);
                  }
                }}
                disabled={!jobType}
              >
                <i className="fa fa-plus"></i> Add
              </button>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const RecruiterServiceCart: React.FC<RecruiterServiceCartProps> = ({
  products,
  job_types,
  create_quote_path,
  form_authenticity_token,
  return_path,
  cancel_url,
}) => {
  const { state, addItem, removeItem, updateJobType, updateProduct } = useCart();
  const [jobType, setJobType] = useState<CartJobType | null>(job_types[0] || null);

  useEffect(() => {
    if (!jobType && job_types.length > 0) {
      setJobType(job_types[0]);
    }
  }, [jobType, job_types]);

  const getItemPrice = (item: CartItem): number =>
    item.jobType.premium ? item.product.premium_price : item.product.price;

  const getItemPlan = (item: CartItem): string =>
    item.jobType.premium ? item.product.premium_id : item.product.stripe_plan;

  const stripeItems = useMemo(
    () =>
      state.items.map(item => ({
        price: getItemPlan(item),
        quantity: item.quantity,
      })),
    [state.items]
  );

  const subtotal = useMemo(
    () => state.items.reduce((total, item) => total + getItemPrice(item) * item.quantity, 0),
    [state.items]
  );

  const request: CheckoutRequest = {
    authenticity_token: form_authenticity_token,
    return_path,
    purchase_name: 'Recruiter Service',
    purchase_amount: subtotal,
    prices: stripeItems,
  };

  return (
    <div className="recruiter-service-cart">
      <div className="d-flex flex-column flex-md-row gap-3">
        <div className="flex-grow-1">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fa fa-shopping-cart me-2"></i>
                Recruiter Services
              </h5>
            </div>
            <div className="card-body">
              <CartItems
                state={state}
                removeItem={removeItem}
                updateJobType={updateJobType}
                updateProduct={updateProduct}
                jobTypes={job_types}
                products={products}
                getItemPrice={getItemPrice}
              />
            </div>
            <div className="card-footer">
              <div className="text-end border-top pt-3">
                <div className="h5 text-primary mb-3">Subtotal: ${subtotal}</div>
                <form action={create_quote_path} method="post">
                  <input type="hidden" name="authenticity_token" value={request.authenticity_token} />
                  <input type="hidden" name="return_path" value={request.return_path} />
                  <input type="hidden" name="purchase_name" value={request.purchase_name} />
                  <input type="hidden" name="purchase_amount" value={request.purchase_amount} />
                  {request.prices.map((price, index) => (
                    <input key={index} type="hidden" name="prices[]" value={price.price} />
                  ))}
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => {
                        window.location.href = cancel_url;
                      }}
                    >
                      <i className="fa fa-arrow-left me-1"></i>
                      Back
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={state.items.length === 0}>
                      <i className="fa fa-credit-card me-1"></i>
                      Checkout
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="fa fa-plus me-2"></i>
                Add Services
              </h6>
            </div>
            <div className="card-body">
              <ProductSelector
                products={products}
                jobTypes={job_types}
                jobType={jobType}
                setJobType={setJobType}
                addItem={addItem}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruiterServiceCart;
